import { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Tooltip,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InventoryIcon from '@mui/icons-material/Inventory';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CategoryIcon from '@mui/icons-material/Category';
import PeopleIcon from '@mui/icons-material/People';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SettingsIcon from '@mui/icons-material/Settings';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import MenuIcon from '@mui/icons-material/Menu';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../store/slices/authSlice';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import { usePermission } from '../../hooks/usePermission';

function MainLayout() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const { hasModuleAccess, isAdmin } = usePermission();

  const allMenuItems = [
    { icon: DashboardIcon, path: '/dashboard', label: 'Dashboard', module: 'dashboard' },
    { icon: InventoryIcon, path: '/products', label: 'Products', module: 'products' },
    { icon: CategoryIcon, path: '/products/categories', label: 'Categories', module: 'products' },
    { icon: WarehouseIcon, path: '/inventory', label: 'Inventory', module: 'inventory' },
    { icon: ShoppingCartIcon, path: '/sales', label: 'Sales', module: 'sales' },
    { icon: DirectionsCarIcon, path: '/van-sales', label: 'Van Sales', module: 'van-sales' },
    { icon: LocalShippingIcon, path: '/purchases', label: 'Purchases', module: 'purchases' },
    { icon: PeopleIcon, path: '/customers', label: 'Customers', module: 'sales' },
    { icon: ReceiptLongIcon, path: '/expenses', label: 'Expenses', module: 'expenses' },
    { icon: PersonIcon, path: '/users', label: 'Users', module: 'users' },
    { icon: SettingsIcon, path: '/roles', label: 'Roles', module: 'roles' },
    { icon: AssessmentIcon, path: '/reports', label: 'Reports', module: 'reports' },
  ];

  const menuItems = allMenuItems.filter((item) => isAdmin || hasModuleAccess(item.module));

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === path;
    }
    if (path === '/products') {
      return (
        (location.pathname === '/products' || location.pathname.startsWith('/products/')) &&
        !location.pathname.startsWith('/products/categories')
      );
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const activeItem = menuItems.find((item) => isActive(item.path));

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(to right, #E8F5E9 0%, #F1F8E9 30%, #FFF9E6 70%, #FFFEF5 100%)',
        backgroundAttachment: 'fixed',
      }}
    >
      {/* Top App Bar */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          background: 'linear-gradient(to right, #E8F5E9 0%, #F1F8E9 30%, #FFF9E6 70%, #FFFEF5 100%)',
          backgroundAttachment: 'fixed',
          color: '#2C3E50',
          boxShadow: 'none',
          border: 'none',
          backdropFilter: 'none',
        }}
      >
        <Toolbar
          sx={{
            justifyContent: 'space-between',
            px: { xs: 2, sm: 3, md: 4, lg: 6 },
            py: { xs: 1, sm: 1.5 },
            gap: { xs: 1, sm: 2 },
            flexWrap: { xs: 'nowrap', sm: 'wrap' },
            minHeight: { xs: 64, sm: 70 },
          }}
        >
          {/* Left: Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Box
              sx={{
                width: { xs: 32, sm: 36, md: 40 },
                height: { xs: 32, sm: 36, md: 40 },
                borderRadius: 3,
                background: 'linear-gradient(135deg, #4ECDC4 0%, #44A08D 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(78, 205, 196, 0.3)',
                flexShrink: 0,
              }}
            >
              <LocalFloristIcon sx={{ color: 'white', fontSize: { xs: 20, sm: 24, md: 28 } }} />
            </Box>
            <Typography
              variant="h5"
              component="div"
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #4ECDC4 0%, #44A08D 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                display: { xs: 'none', sm: 'block' },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              YoroInventory
            </Typography>
          </Box>

          {/* Center: Icon Navigation Menu */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              gap: { md: 1, lg: 1.5 },
              flex: 1,
              justifyContent: 'center',
              mx: { md: 2, lg: 4 },
              overflowX: 'auto',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              scrollbarWidth: 'none',
            }}
          >
            {menuItems.map((item) => {
              const IconComponent = item.icon;
              const active = isActive(item.path);
              return (
                <Tooltip key={item.path} title={item.label} arrow>
                  <IconButton
                    onClick={() => navigate(item.path)}
                    sx={{
                      width: { md: 40, lg: 48 },
                      height: { md: 40, lg: 48 },
                      borderRadius: '50%',
                      backgroundColor: active ? '#4ECDC4' : 'white',
                      color: active ? 'white' : '#2C3E50',
                      border: active ? 'none' : '1px solid rgba(0,0,0,0.05)',
                      boxShadow: active
                        ? '0 4px 12px rgba(78, 205, 196, 0.3)'
                        : '0 2px 4px rgba(0,0,0,0.05)',
                      transition: 'all 0.3s ease',
                      flexShrink: 0,
                      '&:hover': {
                        backgroundColor: active ? '#3BA89F' : '#F8F9FA',
                        boxShadow: active
                          ? '0 6px 16px rgba(78, 205, 196, 0.4)'
                          : '0 4px 8px rgba(0,0,0,0.1)',
                        transform: 'scale(1.05)',
                      },
                      '& .MuiSvgIcon-root': {
                        fontSize: { md: 20, lg: 24 },
                      },
                    }}
                  >
                    <IconComponent />
                  </IconButton>
                </Tooltip>
              );
            })}
          </Box>
          
          {/* Mobile Navigation Menu Button */}
          <IconButton
            sx={{
              display: { xs: 'flex', md: 'none' },
              color: '#2C3E50',
              backgroundColor: 'white',
              '&:hover': {
                backgroundColor: '#F8F9FA',
              },
            }}
            onClick={handleMobileMenuToggle}
          >
            <MenuIcon />
          </IconButton>

          {/* Right: User Info */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
            {/* Notification Bell Icon */}
            <IconButton
              sx={{
                width: { xs: 36, sm: 40 },
                height: { xs: 36, sm: 40 },
                backgroundColor: 'white',
                color: '#2C3E50',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid rgba(0,0,0,0.05)',
                borderRadius: '50%',
                position: 'relative',
                '&:hover': {
                  backgroundColor: '#F8F9FA',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <Badge
                badgeContent={3}
                sx={{
                  '& .MuiBadge-badge': {
                    backgroundColor: '#F44336',
                    color: 'white',
                    width: { xs: 16, sm: 18 },
                    height: { xs: 16, sm: 18 },
                    minWidth: { xs: 16, sm: 18 },
                    borderRadius: '2px',
                    fontSize: { xs: '0.65rem', sm: '0.7rem' },
                    fontWeight: 700,
                    boxShadow: '0 2px 4px rgba(244, 67, 54, 0.4)',
                    top: 2,
                    right: 2,
                  },
                }}
              >
                <NotificationsIcon sx={{ fontSize: { xs: 22, sm: 24, md: 26 }, color: '#2C3E50' }} />
              </Badge>
            </IconButton>

            {/* User icon - opens menu (Profile, Settings, Logout) */}
            <IconButton
              onClick={handleMenuOpen}
              sx={{
                p: 0,
                '&:hover': {
                  transform: 'scale(1.05)',
                },
                transition: 'transform 0.2s',
              }}
            >
              <Avatar
                sx={{
                  bgcolor: '#4ECDC4',
                  width: { xs: 36, sm: 40 },
                  height: { xs: 36, sm: 40 },
                  boxShadow: '0 2px 8px rgba(78, 205, 196, 0.3)',
                  border: '2px solid white',
                }}
              >
                {user?.firstName?.[0]?.toUpperCase() || 'U'}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                sx: {
                  borderRadius: 1,
                  mt: 1.5,
                  minWidth: 160,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  border: '1px solid rgba(0,0,0,0.06)',
                  py: 0.5,
                  '& .MuiMenuItem-root': {
                    py: 1.5,
                    px: 2,
                    fontSize: '0.875rem',
                    gap: 1.5,
                    '&:hover': {
                      backgroundColor: 'rgba(78, 205, 196, 0.08)',
                    },
                  },
                },
              }}
            >
              <MenuItem
                onClick={() => {
                  navigate('/profile');
                  handleMenuClose();
                }}
              >
                <AccountCircleIcon sx={{ fontSize: 20, color: '#4ECDC4' }} />
                Profile
              </MenuItem>
              <MenuItem
                onClick={() => {
                  navigate('/settings');
                  handleMenuClose();
                }}
              >
                <SettingsIcon sx={{ fontSize: 20, color: '#4ECDC4' }} />
                Settings
              </MenuItem>
              <Divider sx={{ my: 0.5 }} />
              <MenuItem
                onClick={handleLogout}
                sx={{ color: '#F44336', '&:hover': { backgroundColor: 'rgba(244, 67, 54, 0.08)' } }}
              >
                <LogoutIcon sx={{ fontSize: 20 }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Navigation Drawer */}
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={handleMobileMenuClose}
        PaperProps={{
          sx: {
            width: 280,
            background: 'linear-gradient(to right, #E8F5E9 0%, #F1F8E9 30%, #FFF9E6 70%, #FFFEF5 100%)',
          },
        }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #4ECDC4 0%, #44A08D 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(78, 205, 196, 0.3)',
              }}
            >
              <LocalFloristIcon sx={{ color: 'white', fontSize: 28 }} />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #4ECDC4 0%, #44A08D 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              YoroInventory
            </Typography>
          </Box>
        </Box>
        <List sx={{ pt: 2 }}>
          {menuItems.map((item) => {
            const IconComponent = item.icon;
            const active = isActive(item.path);
            return (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  onClick={() => {
                    navigate(item.path);
                    handleMobileMenuClose();
                  }}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 2,
                    backgroundColor: active ? '#4ECDC4' : 'transparent',
                    color: active ? 'white' : '#2C3E50',
                    '&:hover': {
                      backgroundColor: active ? '#3BA89F' : 'rgba(78, 205, 196, 0.1)',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: active ? 'white' : '#2C3E50',
                      minWidth: 40,
                    }}
                  >
                    <IconComponent />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontWeight: active ? 600 : 400,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Drawer>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          px: { xs: 2, sm: 3, md: 4, lg: 6 },
          py: { xs: 2, sm: 3, md: 4 },
          maxWidth: '100%',
          minHeight: 'calc(100vh - 80px)',
          overflowX: 'hidden',
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}

export default MainLayout;
